import { useEffect, useRef, useState } from 'react';

import { usePathname } from 'next/navigation';

import { useClientHasRendered } from '../hooks';

export function useIsScrollingDown(offset = 0, bypass = false): boolean {
  const hasClientRendered = useClientHasRendered();
  const prevScrollY = useRef(0);
  const [isScrollingDown, setIsScrollingDown] = useState(false);
  const pathname = usePathname();
  let throttleTimeout: null | NodeJS.Timeout = null;

  const onScrollWithThrottle = (): void => {
    throttleTimeout = null;
    const currentPosition = window.scrollY;

    // current position can be minus for safari browsers in Iphone devices
    if (currentPosition <= 0) {
      setIsScrollingDown(false);
    } else if (currentPosition > prevScrollY.current + offset) {
      if (!isScrollingDown) {
        setIsScrollingDown(true);
      }

      prevScrollY.current = currentPosition;
    } else if (currentPosition + offset < prevScrollY.current) {
      if (isScrollingDown) {
        setIsScrollingDown(false);
      }

      prevScrollY.current = currentPosition;
    }
  };

  useEffect(() => {
    setIsScrollingDown(false);
  }, [pathname]);

  useEffect(() => {
    const onScroll = (): void => {
      // We throttle to callback to avoid too much jankiness
      if (throttleTimeout === null) {
        // eslint-disable-next-line react-hooks/exhaustive-deps
        throttleTimeout = setTimeout(onScrollWithThrottle, 100);
      }
    };
    // Delays in the CSS render can cause the calculation to be off, so this ensures we
    // wait until things have rendered
    if (!bypass && hasClientRendered) {
      document.addEventListener('scroll', onScroll);
    }

    return () => {
      document.removeEventListener('scroll', onScroll);
    };
  }, [bypass, hasClientRendered, isScrollingDown, offset]);

  if (bypass) {
    return false;
  }

  return isScrollingDown;
}
